import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { api, tokenStore } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * PermissionsContext — single source of truth for the matrix pulled from
 * `GET /api/me/permissions`. Consumed by `<CanAccess>`, `<ProtectedRoute>`,
 * `<RestrictedField>`, and the Layout sidebar.
 */

// permission → integer rank (matches backend rbac_matrix._LEVEL_ORDER)
const LEVEL_RANK = {
  none: 0,
  read_status_only: 1,
  read_limited: 1,
  read: 1,
  write: 2,
  admin: 3,
};

const REQUIRED_RANK = { read: 1, write: 2, admin: 3 };

const PermissionsContext = createContext(null);

export function PermissionsProvider({ children }) {
  const { user } = useAuth();
  // state: undefined = loading, null = anonymous, object = ready
  const [perms, setPerms] = useState(undefined);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!tokenStore.get()) {
      setPerms(null);
      return;
    }
    try {
      const { data } = await api.get("/me/permissions");
      setPerms({
        role: data.role,
        roleCode: data.role_code,
        isSuperAdmin: Boolean(data.is_super_admin),
        assignedProjectIds: data.assigned_project_ids || [],
        modules: data.modules || {},
        redactions: data.redactions || {},
        journeyStageVisibility: data.journey_stage_visibility ?? null,
      });
      setError(null);
    } catch (e) {
      setError(e);
      setPerms(null);
    }
  }, []);

  useEffect(() => {
    // Re-fetch matrix whenever the auth user changes (login / logout / session refresh).
    if (user === undefined) {
      // Auth still loading — wait
      return;
    }
    if (user === null) {
      setPerms(null);
      return;
    }
    load();
  }, [user, load]);

  const value = useMemo(
    () => ({
      perms,
      loading: perms === undefined,
      error,
      refresh: load,
    }),
    [perms, error, load]
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

export function usePermissions() {
  const ctx = useContext(PermissionsContext);
  if (!ctx) throw new Error("usePermissions must be used within PermissionsProvider");
  return ctx;
}

/**
 * useCan(module, action)  → boolean
 * action: 'read' | 'write' | 'admin' (default 'read')
 * Modifiers (read_status_only / read_limited) satisfy 'read'.
 */
export function useCan(module, action = "read") {
  const { perms } = usePermissions();
  if (!perms) return false;
  if (perms.isSuperAdmin) return true;
  const perm = perms.modules?.[module] || "none";
  const have = LEVEL_RANK[perm] ?? 0;
  const want = REQUIRED_RANK[action] ?? 1;
  return have >= want;
}

/**
 * usePermissionLevel(module) → raw permission string
 * Useful for `RestrictedField` to detect `read_status_only` / `read_limited`.
 */
export function usePermissionLevel(module) {
  const { perms } = usePermissions();
  if (!perms) return "none";
  if (perms.isSuperAdmin) return "admin";
  return perms.modules?.[module] || "none";
}

/**
 * useHasProjectScope() → boolean
 * True if user has at least one assigned project OR is an all-projects role
 * (super admin / management). False = "no project assigned" empty state trigger.
 */
export function useHasProjectScope() {
  const { perms } = usePermissions();
  if (!perms) return true; // don't block during load
  if (perms.isSuperAdmin) return true;
  if ((perms.roleCode || "").toUpperCase() === "MANAGEMENT") return true;
  return (perms.assignedProjectIds || []).length > 0;
}
